import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormStepProps, ApplicationFormData } from '../../types/application';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
    gap: 30px;
  }
`;

const MainForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  
  &.required::after {
    content: ' *';
    color: #d32f2f;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.span`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;

const OwnershipGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.4;
  
  input[type="radio"] {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.primaryColor} 0%, ${props => props.theme.primaryColor}dd 100%);
  color: white;
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
  
  @media (min-width: 768px) {
    padding: 20px;
    margin: 20px 0;
  }
  
  h4 {
    margin: 0 0 10px 0;
    font-size: 1rem;
    
    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    opacity: 0.9;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
  
  a {
    color: white;
    text-decoration: underline;
    
    &:hover {
      text-decoration: none;
    }
  }
`;

const SidebarTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  font-size: 1.1rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
    padding-left: 16px;
    position: relative;
    
    &::before {
      content: '•';
      color: ${props => props.theme.primaryColor};
      position: absolute;
      left: 0;
    }
  }
`;

const OwnershipInformation: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange
}) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      majorOwners: data.majorOwners,
      ownershipPercentages: data.ownershipPercentages,
      directors: data.directors,
      multicurrencyBanking: data.multicurrencyBanking
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    updateData(watchedValues as Partial<ApplicationFormData>);
  }, [watchedValues, updateData]);

  useEffect(() => {
    // Simple validation - check required fields
    const hasRequiredFields = watchedValues.majorOwners;
    onValidationChange(!!hasRequiredFields);
  }, [watchedValues, onValidationChange]);

  return (
    <StepContainer>
      <StepTitle>Ownership Structure & Banking</StepTitle>
      <StepDescription>
        Provide ownership details and select banking services. All major owners (over 25% shareholding) must be listed for compliance purposes.
      </StepDescription>

      <FormGrid>
        <MainForm>
          <FormGroup>
            <Label className="required">Major Owners (over 25% share percentage shareholders)' Legal Names</Label>
            <TextArea
              {...register('majorOwners')}
              placeholder="You must list all the Legal Names of All Major Owners, separate by comma ','"
              hasError={!!errors.majorOwners}
            />
            {errors.majorOwners && <ErrorMessage>{errors.majorOwners.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Ownership Percentages</Label>
            <OwnershipGrid>
              <div>
                <Input
                  {...register('ownershipPercentages.owner1')}
                  placeholder="Major Owner 1's % of shares (e.g. 50%)"
                />
              </div>
              <div>
                <Input
                  {...register('ownershipPercentages.owner2')}
                  placeholder="Major Owner 2's % of shares (e.g. 25%)"
                />
              </div>
              <div>
                <Input
                  {...register('ownershipPercentages.owner3')}
                  placeholder="Major Owner 3's % of shares (e.g. 25%)"
                />
              </div>
            </OwnershipGrid>
          </FormGroup>

          <FormGroup>
            <Label>Director(s)' Name if different from Major Owners</Label>
            <TextArea
              {...register('directors')}
              placeholder="You must list all the Legal Names of All Directors, separate by comma ','"
            />
          </FormGroup>

          <FormGroup>
            <Label className="required">Multicurrency Banking (Enhanced Compliance/Transaction Monitoring)</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  {...register('multicurrencyBanking')}
                  value="true"
                />
                <div>
                  <strong>I wish to open a FREE Multicurrency Account</strong> and have read and I agree with the Terms below
                </div>
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  {...register('multicurrencyBanking')}
                  value="false"
                />
                Not for now
              </RadioOption>
            </RadioGroup>
          </FormGroup>

          <HighlightBox>
            <h4>Multicurrency Banking Benefits</h4>
            <p>
              If you have overseas suppliers or contractors to pay (in multiple currencies including Euro, Dollar, CNY, GBP), 
              we can open a Multicurrency Bank Account for you. Once approved by Compliance, you receive secure banking 
              facilities with global settlement capabilities. All your Chinese payment settlements will go to your designated account by default.
            </p>
            <p style={{ marginTop: '10px' }}>
              <a href="https://hubfx.co/terms" target="_blank" rel="noopener noreferrer">
                View Multicurrency Banking Terms →
              </a>
            </p>
          </HighlightBox>
        </MainForm>

        <Sidebar>
          <SidebarTitle>Compliance Requirements</SidebarTitle>
          <InfoList>
            <li>All major owners (&gt;25%) must be listed</li>
            <li>Personal documents required for each owner</li>
            <li>Directors must be identified separately</li>
            <li>Ownership percentages should total 100%</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Document Requirements</SidebarTitle>
          <InfoList>
            <li>Current photo ID for all major owners</li>
            <li>Address proof for all major owners</li>
            <li>Company ownership structure diagram</li>
            <li>Shareholding certificates if applicable</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Multicurrency Banking</SidebarTitle>
          <InfoList>
            <li>Support for EUR, USD, CNY, GBP</li>
            <li>Real-time currency conversion</li>
            <li>Global wire transfer capabilities</li>
            <li>Enhanced transaction monitoring</li>
            <li>FREE setup and maintenance</li>
          </InfoList>
        </Sidebar>
      </FormGrid>
    </StepContainer>
  );
};

export default OwnershipInformation;